import { Navigate, Outlet, useLocation } from 'react-router-dom';

import { Box, CircularProgress, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { WButton } from '@wareclouds/wareclouds-ui';

import { useGetLeadEvents, useOnboardingToken } from '../features/onboarding';
import {
  getOnboardingResumePath,
  isOnboardingPathAllowed,
} from '@/features/onboarding/utils/onboardingResumePath';
import { isLeadRequestUnauthorized } from '@/libs/axios/leadAxiosInstance';

const OnboardingTokenGuard = () => {
  const { t } = useTranslation(['system', 'button']);
  const location = useLocation();
  const { token, clearToken } = useOnboardingToken();
  const hasToken = Boolean(token);

  const eventsQuery = useGetLeadEvents({
    enabled: hasToken,
  });

  if (!token) {
    return <Navigate to="/onboarding" replace />;
  }

  if (hasToken && eventsQuery.isPending) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="40vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (hasToken && eventsQuery.isError) {
    if (isLeadRequestUnauthorized(eventsQuery.error)) {
      clearToken();
      return <Navigate to="/onboarding" replace />;
    }
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        gap={2}
        minHeight="40vh"
        px={2}
      >
        <Typography align="center" color="text.secondary">
          {t('GENERAL_ERROR', { ns: 'system' })}
        </Typography>
        <WButton
          label={t('retry', { ns: 'button' })}
          variant="contained"
          color="primary"
          onClick={() => eventsQuery.refetch()}
        />
      </Box>
    );
  }

  if (hasToken && eventsQuery.data) {
    const resumePath = getOnboardingResumePath(eventsQuery.data);
    if (!isOnboardingPathAllowed(location.pathname, resumePath)) {
      return <Navigate to={resumePath} replace />;
    }
  }

  return <Outlet />;
};

export default OnboardingTokenGuard;
